import { AppState, GeneratedFiles, Tool, CloudProvider, VariableDefinition } from '../types';

/**
 * Provider configuration metadata for each cloud provider.
 * Source names, version constraints, and required config fields.
 */
const PROVIDER_CONFIG: Record<
  string,
  {
    terraformName: string;
    source: string;
    version: string;
    configBlock: (state: AppState) => string;
  }
> = {
  [CloudProvider.AWS]: {
    terraformName: 'aws',
    source: 'hashicorp/aws',
    version: '~> 5.0',
    configBlock: (state) => `provider "aws" {
  region = "${state.region}"
}`,
  },
  [CloudProvider.AZURE]: {
    terraformName: 'azurerm',
    source: 'hashicorp/azurerm',
    version: '~> 4.0',
    configBlock: (_state) => `provider "azurerm" {
  features {}

  subscription_id = "YOUR_SUBSCRIPTION_ID"
}`,
  },
  [CloudProvider.GCP]: {
    terraformName: 'google',
    source: 'hashicorp/google',
    version: '~> 6.0',
    configBlock: (state) => `provider "google" {
  project = "${state.gcpProjectId || 'YOUR_PROJECT_ID'}"
  region  = "${state.region}"
}`,
  },
  [CloudProvider.OCI]: {
    terraformName: 'oci',
    source: 'oracle/oci',
    version: '~> 6.0',
    configBlock: (state) => `provider "oci" {
  region       = "${state.region}"
  tenancy_ocid = "YOUR_TENANCY_OCID"
  user_ocid    = "YOUR_USER_OCID"
  fingerprint  = "YOUR_FINGERPRINT"
  private_key_path = "~/.oci/oci_api_key.pem"
}`,
  },
};

/**
 * Intelligently formats a value for HCL output.
 * Detects whether the value is already properly formatted or needs quoting.
 */
function formatOverrideValue(value: string, _variables: VariableDefinition[]): string {
  const trimmed = value.trim();

  // Already looks like a structured value — don't touch
  if (trimmed.startsWith('{') || trimmed.startsWith('[') || trimmed.startsWith('<<')) {
    return trimmed;
  }

  // Boolean or null
  if (['true', 'false', 'null'].includes(trimmed.toLowerCase())) {
    return trimmed.toLowerCase();
  }

  // Number
  if (/^-?\d+(\.\d+)?(e[+-]?\d+)?$/i.test(trimmed)) {
    return trimmed;
  }

  // Function call or reference (e.g., var.xxx, module.xxx, toset(...))
  if (/^[a-z_][a-z_0-9.]*\s*\(/.test(trimmed) || /^(var|module|data|local|each)\./i.test(trimmed)) {
    return trimmed;
  }

  // Already properly quoted
  if (trimmed.startsWith('"') && trimmed.endsWith('"')) {
    return trimmed;
  }

  // Default: quote as string
  return `"${trimmed}"`;
}

/**
 * Generates the provider.tf file content.
 */
function generateProviderFile(state: AppState): string {
  const config = PROVIDER_CONFIG[state.provider];
  if (!config) {
    return `# Unsupported provider: ${state.provider}\n# Please configure manually.`;
  }

  return `terraform {
  required_providers {
    ${config.terraformName} = {
      source  = "${config.source}"
      version = "${config.version}"
    }
  }
}

${config.configBlock(state)}`;
}

/**
 * Generates the main.tf file content with module block and variable mappings.
 */
function generateMainFile(state: AppState): string {
  const mappings = state.stagedOverrides.map((o) => `  ${o.name} = var.${o.name}`).join('\n');

  return `module "this" {
  source = "${state.moduleUrl}"

${mappings}
}`;
}

/**
 * Generates the overrides.tf file content with variable blocks.
 */
function generateOverridesFile(state: AppState): string {
  return state.stagedOverrides
    .map((o) => {
      const varDef = state.variables.find((v) => v.name === o.name);
      const formattedValue = formatOverrideValue(o.value, state.variables);

      // Include type and description from original definition for documentation
      const typeComment = varDef?.type && varDef.type !== 'any' ? `  # type: ${varDef.type}\n` : '';

      return `variable "${o.name}" {${typeComment ? '\n' + typeComment : ''}
  default = ${formattedValue}
}`;
    })
    .join('\n\n');
}

/**
 * Generates Terragrunt-specific provider file (standalone providers.tf).
 */
function generateTerragruntProviderFile(state: AppState): string {
  const config = PROVIDER_CONFIG[state.provider];
  if (!config) {
    return `# Unsupported provider: ${state.provider}`;
  }
  return config.configBlock(state);
}

/**
 * Generates terragrunt.hcl content.
 */
function generateTerragruntMainFile(state: AppState): string {
  return `terraform {
  source = "${state.moduleUrl}"

  extra_arguments "always_local_plan" {
    commands  = ["plan"]
    arguments = ["-out=./saved-plan"]
  }

  extra_arguments "force_saved_plan" {
    commands  = ["apply"]
    arguments = ["./saved-plan"]
  }
}`;
}

/**
 * Generates the overrides.tf for Terragrunt (same format as Terraform).
 */
function generateTerragruntOverridesFile(state: AppState): string {
  return generateOverridesFile(state);
}

/**
 * Main entry point: generates all configuration files locally using templates.
 * No AI, no network calls. 100% client-side, deterministic output.
 */
export function generateFilesLocally(state: AppState): GeneratedFiles {
  const isTerragrunt = state.tool === Tool.TERRAGRUNT;

  if (isTerragrunt) {
    return {
      provider: generateTerragruntProviderFile(state),
      main: generateTerragruntMainFile(state),
      overrides: generateTerragruntOverridesFile(state),
    };
  }

  // Terraform and OpenTofu use the same wrapper module pattern
  return {
    provider: generateProviderFile(state),
    main: generateMainFile(state),
    overrides: generateOverridesFile(state),
  };
}
